import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Trash2, Pencil, ExternalLink } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import EscanEatScore from './ScanEatScore';
import ProductAnalysis from './ProductAnalysis';
import EditProduct from './EditProduct';
import ShareButtons from '@/components/social/ShareButtons';

export default function ProductDetail({ product, onClose, onDelete, onEdit }) {
    const [showEdit, setShowEdit] = useState(false);
    
    if (!product) return null;

    const handleEdit = async (updatedData) => {
        await onEdit(product.id, updatedData);
        setShowEdit(false);
    };

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center sm:p-4"
                onClick={onClose}
            >
                <motion.div
                    initial={{ opacity: 0, y: 100 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 100 }}
                    transition={{ type: "spring", damping: 25, stiffness: 300 }}
                    onClick={(e) => e.stopPropagation()}
                    className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-lg overflow-hidden"
                    style={{ maxHeight: '92dvh' }}
                >
                    {/* Header */}
                    <div className="relative">
                        {product.image_url && (
                            <div className="h-32 bg-gray-100">
                                <img 
                                    src={product.image_url} 
                                    alt={product.name}
                                    className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                            </div>
                        )}
                        
                        <div className="absolute top-4 right-4 flex gap-2">
                          <ShareButtons product={product} type="icon" />
                          <button
                            onClick={onClose}
                            className="w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-colors shadow-lg"
                          >
                            <X className="w-5 h-5 text-gray-600" />
                          </button>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="p-4 sm:p-6 overflow-y-auto" style={{ maxHeight: 'calc(92dvh - 8rem)' }}>
                        {/* Title */}
                        <div className="mb-6">
                            <h2 className="text-2xl font-bold text-gray-900">{product.name}</h2>
                            {product.brand && (
                                <p className="text-gray-500 mt-1">{product.brand}</p>
                            )}
                            {product.category && (
                                <span className="inline-flex items-center px-3 py-1 rounded-full bg-gray-100 text-gray-600 text-sm mt-3">
                                    {product.category}
                                </span>
                            )}
                            {product.user_edited && (
                                <p className="text-xs text-blue-600 mt-2">✓ Editado por ti</p>
                            )}

                            {product.manufacturer_url && (
                                <a
                                    href={product.manufacturer_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 mt-4 text-sm text-blue-600 hover:text-blue-700"
                                >
                                    <ExternalLink className="w-4 h-4" />
                                    Fabricante
                                </a>
                            )}

                            <div className="flex gap-3 mt-4">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setShowEdit(true)}
                                    className="flex items-center gap-2"
                                >
                                    <Pencil className="w-4 h-4" />
                                    Editar
                                </Button>
                                <Button
                                    onClick={() => onDelete(product.id)}
                                    variant="outline"
                                    size="sm"
                                    className="text-red-600 border-red-200 hover:bg-red-50 flex items-center gap-2"
                                >
                                    <Trash2 className="w-4 h-4" />
                                    Eliminar
                                </Button>
                            </div>
                        </div>

                        {/* EscanEat Score */}
                        {product.scaneat_score !== undefined && (
                            <div className="mb-6">
                                <EscanEatScore 
                                    score={product.scaneat_score} 
                                    trafficLight={product.traffic_light}
                                />
                            </div>
                        )}

                        {/* Analysis */}
                        <ProductAnalysis product={product} />

                        {/* Ingredients */}
                        {product.ingredients && (
                            <div className="mt-6 p-4 bg-gray-50 rounded-xl">
                                <h3 className="font-semibold text-gray-900 mb-2 text-sm">Ingredientes</h3>
                                <p className="text-sm text-gray-600">{product.ingredients}</p>
                            </div>
                        )}

                        <div className="mt-6 pt-4 border-t border-gray-100">
                            <span className="text-sm text-gray-400">
                                {format(new Date(product.created_date), "d 'de' MMMM, yyyy", { locale: es })}
                            </span>
                        </div>
                    </div>
                </motion.div>

                {showEdit && (
                    <EditProduct
                        product={product}
                        onSave={handleEdit}
                        onClose={() => setShowEdit(false)}
                    />
                )}
            </motion.div>
        </AnimatePresence>
    );
}